// Locale-aware accessors for the /[lang]/[city]/events and /[lang]/[city]/tours
// pages' hand-written per-city copy. Same approach as boatRentalCityCopy
// (boat-rental-content.ts): read off the imported resource JSON directly, since
// `intro` is an array (no `returnObjects` dance through t()).

import type { Locale } from '@todo/core/i18n/locales';
import en from '@/lib/i18n/resources/en.json';
import fr from '@/lib/i18n/resources/fr.json';
import ru from '@/lib/i18n/resources/ru.json';

export type CityListingCopy = {
  /** On-page <h1>, and the anchor text other pages link this page with. */
  title: string;
  /** <title> base — fitted to the SERP budget by seoTitle() in seo.ts. */
  seoTitle: string;
  intro: string[];
};

type CityListingsResource = {
  cityEvents: { cities: Record<string, CityListingCopy> };
  cityTours: { cities: Record<string, CityListingCopy> };
  boatRental: { cities: Record<string, { inCity: string }> };
};

const resources: Record<Locale, CityListingsResource> = { en, fr, ru };

/** A city's /[city]/events page copy, or undefined for a city without authored copy. */
export function cityEventsCopy(locale: Locale, cityId: string): CityListingCopy | undefined {
  return resources[locale].cityEvents.cities[cityId];
}

/** A city's /[city]/tours page copy, or undefined for a city without authored copy. */
export function cityToursCopy(locale: Locale, cityId: string): CityListingCopy | undefined {
  return resources[locale].cityTours.cities[cityId];
}

/**
 * "in Nice" / "à Nice" / "в Ницце", interpolated as {{inCity}} into the shared
 * strings. Declined per city, so it can't be a `{{city}}` interpolation of the
 * Latin name — reuses the boat-rental pages' authored phrase rather than
 * keeping a second copy of the same seven declensions.
 */
export function cityInPhrase(locale: Locale, cityId: string): string | undefined {
  return resources[locale].boatRental.cities[cityId]?.inCity;
}
